import { useCallback, useEffect, useState } from 'react';
import { api, formatDateTime, type Post, type Publication, type Recurrence } from '../api.js';
import { PageHead } from './PageHead.js';

const POST_BADGE: Record<Post['status'], { cls: string; label: string }> = {
  DRAFT: { cls: '', label: 'rascunho' },
  SCHEDULED: { cls: 'accent', label: 'agendado' },
  PUBLISHING: { cls: 'warn', label: 'publicando' },
  PUBLISHED: { cls: 'ok', label: 'publicado' },
  PARTIAL: { cls: 'warn', label: 'parcial' },
  FAILED: { cls: 'err', label: 'falhou' },
  CANCELED: { cls: '', label: 'cancelado' },
};

const PUB_BADGE: Record<Publication['status'], { cls: string; label: string }> = {
  PENDING: { cls: '', label: 'na fila' },
  PUBLISHING: { cls: 'warn', label: 'enviando' },
  PUBLISHED: { cls: 'ok', label: 'publicado' },
  FAILED: { cls: 'err', label: 'erro' },
  SKIPPED: { cls: '', label: 'pulado' },
  AWAITING_MANUAL: { cls: 'warn', label: 'postar à mão' },
};

const PLATFORM_ICON: Record<Publication['platform'], string> = {
  INSTAGRAM: '📷',
  TIKTOK: '🎵',
  WHATSAPP: '💬',
};

type Filter = 'proximos' | 'publicados' | 'problemas' | 'todos';

/**
 * Distância até o horário agendado, em linguagem de fila.
 *
 * "em 2h14" diz mais que "23/08 20:13" para quem só quer saber se dá tempo de
 * trocar a mídia. Quando já passou, dizemos que está saindo — o worker roda a
 * cada minuto, então o atraso é de segundos, não de horas.
 */
function faltaPara(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'saindo';
  const min = Math.round(ms / 60_000);
  if (min < 60) return `em ${min}min`;
  const h = Math.floor(min / 60);
  const resto = min % 60;
  if (h < 24) return resto ? `em ${h}h${String(resto).padStart(2, '0')}` : `em ${h}h`;
  return `em ${Math.round(h / 24)}d`;
}

export function Schedule() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [recorrencias, setRecorrencias] = useState<Recurrence[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('proximos');
  const [copied, setCopied] = useState<string | null>(null);
  /** Post em edição de texto, com o rascunho local até salvar. */
  const [editando, setEditando] = useState<{ id: string; texto: string } | null>(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.posts(),
        // Falha aqui não pode esconder a fila: a lista de posts é o essencial.
        api.recurrences().catch(() => [] as Recurrence[]),
      ]);
      setPosts(p);
      setRecorrencias(r);
    } finally {
      setLoading(false);
    }
  }, []);

  async function alternarRecorrencia(r: Recurrence) {
    setRecorrencias((prev) =>
      prev.map((x) => (x.id === r.id ? { ...x, active: !x.active } : x)),
    );
    try {
      await api.toggleRecurrence(r.id, !r.active);
      await load(); // retomar materializa posts novos; a fila precisa refletir
    } catch {
      await load();
    }
  }

  async function removerRecorrencia(r: Recurrence) {
    const ok = window.confirm(
      `Remover a programação de “${r.product.name}”?

` +
        'As ocorrências que ainda não foram publicadas são canceladas. ' +
        'O que já foi publicado fica no histórico.',
    );
    if (!ok) return;
    try {
      await api.deleteRecurrence(r.id);
    } finally {
      await load();
    }
  }

  useEffect(() => {
    void load();
    // O worker roda a cada minuto; recarregamos para o status mudar sozinho na tela.
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const proximos = posts.filter((p) => p.status === 'SCHEDULED' || p.status === 'DRAFT');
  const problemas = posts.filter((p) => p.status === 'FAILED' || p.status === 'PARTIAL');
  const aguardandoMao = posts.filter((p) =>
    p.publications.some((pub) => pub.status === 'AWAITING_MANUAL'),
  );
  // O próximo da fila é a informação que a pessoa mais busca ao abrir a Agenda:
  // "falta quanto para sair?".
  const proximo = proximos
    .filter((p) => p.scheduledAt)
    .sort(
      (a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime(),
    )[0];

  const visible = posts.filter((post) => {
    if (filter === 'todos') return true;
    if (filter === 'proximos') return post.status === 'SCHEDULED' || post.status === 'DRAFT';
    if (filter === 'publicados') return post.status === 'PUBLISHED';
    return post.status === 'FAILED' || post.status === 'PARTIAL';
  });

  async function salvarEdicao() {
    if (!editando) return;
    setSalvandoEdicao(true);
    try {
      await api.updatePost(editando.id, { caption: editando.texto });
      setEditando(null);
      await load();
    } catch (cause) {
      // Mantém o editor aberto com o texto digitado: fechar descartaria o que
      // a pessoa escreveu por causa de uma falha de rede.
      window.alert(cause instanceof Error ? cause.message : 'Falha ao salvar o texto');
    } finally {
      setSalvandoEdicao(false);
    }
  }

  async function cancel(id: string) {
    await api.cancelPost(id).catch(() => undefined);
    await load();
  }

  async function remove(id: string) {
    await api.deletePost(id).catch(() => undefined);
    await load();
  }

  async function copyMessage(post: Post, pub: Publication) {
    const text = `${post.caption}\n\n${pub.affiliateLink ?? ''}`.trim();
    await navigator.clipboard.writeText(text).catch(() => undefined);
    setCopied(pub.id);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <>
      <PageHead
        eyebrow="Agenda"
        title="A fila do dia"
        description="O worker verifica a cada minuto e publica no horário — esta tela se atualiza sozinha."
        readout={[
          { label: 'na fila', value: proximos.length },
          proximo?.scheduledAt && {
            label: 'próximo',
            value: faltaPara(proximo.scheduledAt),
          },
          aguardandoMao.length > 0 && {
            label: 'esperando você',
            value: aguardandoMao.length,
            tom: 'quiet' as const,
          },
          problemas.length > 0 && {
            label: problemas.length === 1 ? 'com problema' : 'com problemas',
            value: problemas.length,
            tom: 'loss' as const,
          },
        ]}
        actions={
          <>
          {(
            [
              ['proximos', 'Próximos'],
              ['publicados', 'Publicados'],
              ['problemas', 'Problemas'],
              ['todos', 'Todos'],
            ] as Array<[Filter, string]>
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className="tab"
              aria-current={filter === id ? 'page' : undefined}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              aria-label="Recarregar a fila"
              title="Recarregar"
            >
              {loading ? <span className="spinner" /> : '↻'}
            </button>
          </>
        }
      />

      {/* ------------------------------------------- programações recorrentes

          Ficam ACIMA da fila porque respondem a outra pergunta: a fila diz "o
          que sai agora", a programação diz "o que vai continuar saindo". Sem
          este bloco, criar uma programação seria irreversível pela interface. */}
      {recorrencias.length > 0 && (
        <section className="card" style={{ padding: 16, marginBottom: 18 }}>
          <p className="section-title">
            Programações recorrentes{' '}
            <span className="muted" style={{ fontWeight: 400 }}>
              ({recorrencias.filter((r) => r.active).length} ativa(s))
            </span>
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            {recorrencias.map((r) => (
              <div
                key={r.id}
                className="row"
                style={{
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-control)',
                  opacity: r.active ? 1 : 0.6,
                }}
              >
                {r.product.imageUrl ? (
                  <img
                    src={r.product.imageUrl}
                    alt=""
                    className="post-thumb"
                    style={{ flex: '0 0 auto' }}
                  />
                ) : (
                  <div className="post-thumb" style={{ flex: '0 0 auto' }} />
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.product.name}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {r.times.join(' · ')} ·{' '}
                    {r.weekdays.length === 0
                      ? 'todos os dias'
                      : r.weekdays
                          .slice()
                          .sort()
                          .map((d) => ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][d])
                          .join(', ')}{' '}
                    ·{' '}
                    {/* "sem prazo" precisa aparecer com essas palavras: é o que
                        distingue de uma programação que vai parar sozinha. */}
                    {r.endsOn
                      ? `até ${new Date(r.endsOn).toLocaleDateString('pt-BR')}`
                      : 'sem prazo'}{' '}
                    · {r.platforms.join(', ').toLowerCase()}
                  </div>
                </div>

                <span
                  className={`badge ${r.active ? 'ok' : ''}`}
                  style={{ flex: '0 0 auto' }}
                >
                  {r.active ? 'ativa' : 'pausada'}
                </span>
                <span className="muted" style={{ flex: '0 0 auto', fontSize: 12 }}>
                  {r._count.posts} post(s) gerado(s)
                </span>

                <button
                  type="button"
                  style={{ flex: '0 0 auto' }}
                  onClick={() => void alternarRecorrencia(r)}
                >
                  {r.active ? 'Pausar' : 'Retomar'}
                </button>
                <button
                  type="button"
                  className="danger"
                  style={{ flex: '0 0 auto' }}
                  onClick={() => void removerRecorrencia(r)}
                >
                  Remover
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {visible.length === 0 ? (
        <div className="card empty">
          <strong>Nada aqui</strong>
          {filter === 'proximos'
            ? 'Agende publicações na aba Produção.'
            : 'Nenhum post neste filtro.'}
        </div>
      ) : (
        <div className="card">
          {visible.map((post) => {
            const badge = POST_BADGE[post.status];
            return (
              <div key={post.id} className="post-row">
                {post.mediaAsset ? (
                  post.mediaAsset.kind === 'VIDEO' ? (
                    <video src={post.mediaAsset.publicUrl} className="post-thumb" muted />
                  ) : (
                    <img src={post.mediaAsset.publicUrl} alt="" className="post-thumb" />
                  )
                ) : post.product.imageUrl ? (
                  <img src={post.product.imageUrl} alt="" className="post-thumb" />
                ) : (
                  <div className="post-thumb" />
                )}

                <div className="post-main">
                  <div
                    style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <strong style={{ fontSize: 13 }}>{post.product.name}</strong>
                    <span className={`badge ${badge.cls}`}>{badge.label}</span>
                    <span className="muted">🕑 {formatDateTime(post.scheduledAt)}</span>
                  </div>

                  {editando?.id === post.id ? (
                    <div style={{ marginTop: 6 }}>
                      <textarea
                        value={editando.texto}
                        onChange={(e) =>
                          setEditando({ id: post.id, texto: e.target.value })
                        }
                        style={{ minHeight: 130, fontSize: 12.5 }}
                        aria-label="Texto da publicação"
                      />
                      <div className="row" style={{ gap: 8, marginTop: 8 }}>
                        <button
                          type="button"
                          className="primary"
                          style={{ flex: '0 0 auto' }}
                          disabled={salvandoEdicao}
                          onClick={() => void salvarEdicao()}
                        >
                          {salvandoEdicao ? <span className="spinner" /> : 'Salvar texto'}
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          style={{ flex: '0 0 auto' }}
                          disabled={salvandoEdicao}
                          onClick={() => setEditando(null)}
                        >
                          Cancelar edição
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="post-caption">
                      {post.caption || <em>sem texto</em>}
                    </div>
                  )}
                  {/* O texto NÃO traz o link de propósito: ele entra no fim da
                      mensagem na hora de publicar, um por rede (é o que separa
                      a comissão por canal). Sem esta linha, a fila parecia
                      incompleta e gerava a dúvida "cadê o link?". */}
                  {(post.status === 'SCHEDULED' || post.status === 'DRAFT') && (
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                      🔗 o link de afiliado entra sozinho no fim da mensagem, na hora de
                      publicar — um link por rede
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    {post.publications.map((pub) => {
                      const pb = PUB_BADGE[pub.status];
                      return (
                        <span
                          key={pub.id}
                          className={`badge ${pb.cls}`}
                          title={pub.error ?? undefined}
                        >
                          {PLATFORM_ICON[pub.platform]} {pb.label}
                          {pub.attempts > 1 && ` (${pub.attempts}x)`}
                        </span>
                      );
                    })}
                  </div>

                  {/* Erros e avisos importam mais que qualquer outra informação
                      desta linha — por isso ficam visíveis, não num tooltip. */}
                  {post.publications
                    .filter((p) => p.error)
                    .map((pub) => (
                      <div
                        key={`${pub.id}-err`}
                        className={`notice ${pub.status === 'FAILED' ? 'err' : 'warn'}`}
                        style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}
                      >
                        <strong>{pub.platform}:</strong> {pub.error}
                      </div>
                    ))}
                </div>

                <div className="post-side">
                  {post.publications
                    .filter((p) => p.status === 'AWAITING_MANUAL')
                    .map((pub) => (
                      <button
                        key={`${pub.id}-copy`}
                        type="button"
                        className="primary"
                        onClick={() => void copyMessage(post, pub)}
                        style={{ fontSize: 12 }}
                      >
                        {copied === pub.id ? '✓ copiado' : '📋 Copiar mensagem'}
                      </button>
                    ))}

                  {post.publications
                    .filter((p) => p.permalink)
                    .map((pub) => (
                      <a
                        key={`${pub.id}-link`}
                        href={pub.permalink!}
                        target="_blank"
                        rel="noreferrer"
                        className="muted"
                      >
                        ver post ↗
                      </a>
                    ))}

                  {/* Editar só faz sentido antes de sair: publicado é histórico. */}
                  {(post.status === 'SCHEDULED' || post.status === 'DRAFT') && (
                    <button
                      type="button"
                      className="ghost"
                      onClick={() =>
                        setEditando(
                          editando?.id === post.id
                            ? null
                            : { id: post.id, texto: post.caption },
                        )
                      }
                    >
                      {editando?.id === post.id ? 'fechar edição' : 'editar texto'}
                    </button>
                  )}
                  {(post.status === 'SCHEDULED' || post.status === 'DRAFT') && (
                    <button type="button" className="ghost" onClick={() => void cancel(post.id)}>
                      cancelar
                    </button>
                  )}
                  {post.status !== 'PUBLISHING' && (
                    <button
                      type="button"
                      className="ghost danger"
                      onClick={() => void remove(post.id)}
                    >
                      excluir
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
